import React, { useEffect, useMemo, useRef, useState } from "react";
import { storage } from "../lib/api";

// Shared "pick an idea" list for chores and rewards.
// Search is always on. The person chooses how to browse categories and we remember it on their device.
const VIEWS = [["chips", "Chips"], ["menu", "Dropdown"], ["tiles", "Tiles"]];
const ACCENTS = {
  primary: { on: "bg-primary text-white", focus: "focus:border-primary", hover: "hover:border-primary/40" },
  coral: { on: "bg-coral text-white", focus: "focus:border-coral", hover: "hover:border-coral/40" },
};

// The popup itself is the only scroller. After a filter change, put it back at the top.
function scrollParentToTop(el) {
  let p = el && el.parentElement;
  while (p) {
    const oy = window.getComputedStyle(p).overflowY;
    if ((oy === "auto" || oy === "scroll") && p.scrollHeight > p.clientHeight) { p.scrollTop = 0; return; }
    p = p.parentElement;
  }
}

export default function TemplatePicker({ templates, kind = "chore", accent = "primary", renderItem, storageKey }) {
  const a = ACCENTS[accent] || ACCENTS.primary;
  const key = storageKey || `cm_picker_view_${kind}`;
  const fallback = kind === "chore" ? "menu" : "chips";
  const [view, setViewState] = useState(() => {
    const v = storage.get(key);
    return VIEWS.some(([k]) => k === v) ? v : fallback;
  });
  const [cat, setCat] = useState("All");
  const [q, setQ] = useState("");
  const topRef = useRef(null);
  const first = useRef(true);

  const setView = (v) => { setViewState(v); storage.set(key, v); setCat("All"); };

  const cats = useMemo(() => {
    const m = new Map();
    templates.forEach((t) => m.set(t.category, (m.get(t.category) || 0) + 1));
    return Array.from(m, ([name, count]) => ({ name, count }));
  }, [templates]);

  const needle = q.trim().toLowerCase();
  const showTiles = view === "tiles" && cat === "All" && !needle;
  const list = useMemo(
    () => templates.filter((t) =>
      (cat === "All" || t.category === cat) &&
      (!needle || String(t.title).toLowerCase().includes(needle) || String(t.category).toLowerCase().includes(needle))),
    [templates, cat, needle]
  );

  useEffect(() => {
    if (first.current) { first.current = false; return; }
    scrollParentToTop(topRef.current);
  }, [cat, needle, view]);

  const chip = (name, label, count) => (
    <button key={name} type="button" onClick={() => setCat(name)} aria-pressed={cat === name}
      className={`shrink-0 px-3 py-1.5 rounded-full text-xs font-bold ${cat === name ? a.on : "bg-slate-100 text-ink-soft"}`}>
      {label} <span className="opacity-70">{count}</span>
    </button>
  );

  return (
    <div ref={topRef} data-testid="template-picker">
      <div className="sticky top-0 z-10 bg-white pb-2 pt-1 -mx-1 px-1">
        <input type="search" value={q} onChange={(e) => setQ(e.target.value)}
          placeholder={kind === "chore" ? "Search chores..." : "Search rewards..."}
          aria-label="Search" data-testid="template-search"
          className={`w-full mb-2 rounded-2xl border-2 border-slate-100 px-3 py-2 text-sm outline-none ${a.focus}`} />
        <div className="flex items-center gap-1.5 text-xs">
          <span className="font-bold uppercase tracking-[0.15em] text-ink-soft mr-1">View</span>
          {VIEWS.map(([k, l]) => (
            <button key={k} type="button" onClick={() => setView(k)} aria-pressed={view === k} data-testid={`view-${k}`}
              className={`px-2.5 py-1 rounded-full font-bold ${view === k ? a.on : "bg-slate-100 text-ink-soft"}`}>{l}</button>
          ))}
        </div>
        {view === "menu" && (
          <select value={cat} onChange={(e) => setCat(e.target.value)} aria-label="Category" data-testid="template-category"
            className="w-full mt-2 rounded-2xl border-2 border-slate-100 bg-white px-3 py-2 text-sm font-bold text-ink">
            <option value="All">{`All categories (${templates.length})`}</option>
            {cats.map((c) => <option key={c.name} value={c.name}>{`${c.name} (${c.count})`}</option>)}
          </select>
        )}
      </div>

      {view === "chips" && (
        <div className="flex flex-wrap gap-1.5 mb-3" data-testid="template-chips">
          {chip("All", "All", templates.length)}
          {cats.map((c) => chip(c.name, c.name, c.count))}
        </div>
      )}

      {showTiles ? (
        <div className="grid grid-cols-2 gap-2 mt-1" data-testid="template-tiles">
          {cats.map((c) => (
            <button key={c.name} type="button" onClick={() => setCat(c.name)}
              className={`p-3 rounded-2xl border-2 border-slate-100 ${a.hover} text-left transition-colors`}>
              <div className="text-sm font-bold text-ink truncate">{c.name}</div>
              <div className="text-xs text-ink-soft">{c.count} ideas</div>
            </button>
          ))}
        </div>
      ) : (
        <div className="mt-1">
          {view === "tiles" && cat !== "All" && (
            <button type="button" onClick={() => setCat("All")} data-testid="tiles-back"
              className="mb-2 text-xs font-bold text-ink-soft">{`< All categories  |  ${cat}`}</button>
          )}
          {list.length === 0 ? (
            <div className="text-center text-sm text-ink-soft py-8" data-testid="template-empty">No matches. Try a different word or category.</div>
          ) : (
            <div className="grid grid-cols-2 gap-2">
              {list.map((t) => <React.Fragment key={t.id}>{renderItem(t)}</React.Fragment>)}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
